import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { errorToastMessage } from "../../utils/toast";
import http from "../../utils/http";
import Header from "./Header";
import StudyConsent from "./StudyConsent";
import Screening from "./Screening";
import { useTranslation } from "react-i18next";

export type StudyParticipantFlags = {
  hasCompletedStudyConsent: boolean;
  hasCompletedStudyScreener: boolean;
};

export const checkStudyParticipantFlags = async (
  studyId: string,
): Promise<StudyParticipantFlags> => {
  const response = await http.get<{
    data: {
      hasCompletedConsent?: boolean;
      hasCompletedScreener?: boolean;
    };
  }>(`/participants/study/${studyId}/flags`);

  const data = response?.data?.data;

  return {
    hasCompletedStudyConsent: data?.hasCompletedConsent ?? false,
    hasCompletedStudyScreener: data?.hasCompletedScreener ?? false,
  };
};

type ViewType = "study-consent" | "study-screening" | "";

const CheckFlags = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [currentView, setCurrentView] = useState<ViewType>("");
  const [toggle, setToggle] = useState(false);
  const completionSourceRef = useRef<ViewType>("");
  const navigate = useNavigate();
  const studyId = localStorage.getItem("study-id") || "";
  const studySlug = localStorage.getItem("study-slug") || "";
  const { t } = useTranslation("participant-flow");

  useEffect(() => {
    async function checkFlagsAndRoute() {
      if (!studyId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const studyFlags = await checkStudyParticipantFlags(studyId);

        if (!studyFlags.hasCompletedStudyConsent) {
          setCurrentView("study-consent");
          setLoading(false);
          return;
        }

        if (!studyFlags.hasCompletedStudyScreener) {
          setCurrentView("study-screening");
          setLoading(false);
          return;
        }

        const lastCompleted = completionSourceRef.current;
        if (lastCompleted === "study-consent") {
          navigate(`/participant/thank-you?type=consent`, {
            replace: true,
          });
        } else if (lastCompleted === "study-screening") {
          navigate(`/participant/thank-you?type=screening`, {
            replace: true,
          });
        } else {
          navigate(`/participant/thank-you`, { replace: true });
        }
      } catch (err) {
        errorToastMessage(err as Error);
        navigate(`/study/${studySlug}`, {
          replace: true,
        });
      } finally {
        setLoading(false);
      }
    }

    checkFlagsAndRoute();
  }, [studyId, navigate, studySlug, toggle]);

  const handleComplete = () => {
    completionSourceRef.current = currentView;
    setToggle((prev) => !prev);
  };

  if (!studyId) {
    return null;
  }

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          bgcolor: "grey.50",
        }}
      >
        <Header />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            p: 2,
          }}
        >
          <CircularProgress
            size={48}
            thickness={2}
            sx={{ color: "primary.main" }}
          />
          <Typography variant="body1" color="text.secondary">
            {t("CHECKING_PROGRESS")}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (currentView === "study-consent") {
    return <StudyConsent onConsentComplete={handleComplete} />;
  }

  if (currentView === "study-screening") {
    return <Screening />;
  }

  return null;
};

export default CheckFlags;
